import { useApolloClient } from "@apollo/client";
import { type DatasetDescription, type SessionV2 } from "@decentriq/core";
import {
  DeprovisionDataLabFromMediaInsightsDcrDocument,
  ProvisionDataLabToMediaInsightsDcrDocument,
} from "@decentriq/graphql/dist/types";
import { isNil } from "lodash";
import { memo, useCallback, useMemo } from "react";
import { useApiCore } from "contexts";
import { type DataLab } from "features/dataLabs/models";
import {
  type DatasetLeafName,
  useMediaDataRoom,
  useMediaDataRoomInsightsData,
} from "features/mediaDataRoom/contexts";
import { MediaDataRoomVersion } from "features/mediaDataRoom/models";
import {
  MediaDataRoomHandlersContext,
  type MediaDataRoomV3HandlersContextValue,
} from "./models";

export type MediaDataRoomHandlersV3WrapperProps = React.PropsWithChildren;

interface CreateDatasetPolicyArgs {
  dataset: DatasetDescription;
  dataRoomId: string;
  session: SessionV2;
  targetLeaf: DatasetLeafName;
}

const createDatasetPolicy = async ({
  session,
  dataset,
  dataRoomId,
  targetLeaf,
}: CreateDatasetPolicyArgs) => {
  const datasetPolicyId = await session.createPolicy({
    v0: {
      allowedComputeContext: {
        dcr: {
          dataRoomId,
          targetLeaf,
        },
      },
      secretSource: {
        secretStore: {
          secretId: dataset.encryptionKeySecretId,
        },
      },
    },
  });

  return datasetPolicyId;
};

const MediaDataRoomHandlersV3Wrapper =
  memo<MediaDataRoomHandlersV3WrapperProps>(({ children }) => {
    const { client } = useApiCore();
    const apolloClient = useApolloClient();
    const { dataRoomId } = useMediaDataRoom(MediaDataRoomVersion.v3);
    const {
      session,
      datasets: { updateAdvertiserDatasetHash, updatePublisherDatasetsHashes },
    } = useMediaDataRoomInsightsData(MediaDataRoomVersion.v3);
    // Seed audience handlers (ex. Advertiser Audience)
    const provisionSeedAudience = useCallback<
      MediaDataRoomV3HandlersContextValue["provisionSeedAudience"]
    >(
      async ({ dataset }) => {
        const seedAudienceDataset = await client.getDataset(
          dataset.datasetHash
        );
        const policyId = await createDatasetPolicy({
          dataRoomId,
          dataset: seedAudienceDataset,
          session: session!,
          targetLeaf: "audiences",
        });
        await session!.sendDataRoomStateActionRequest(dataRoomId, {
          kind: "media",
          provisionSeedAudiencePolicyIds: {
            audiencesPolicyId: policyId,
          },
        });
        updateAdvertiserDatasetHash(dataset.datasetHash);
      },
      [client, dataRoomId, session, updateAdvertiserDatasetHash]
    );
    const deprovisionSeedAudience = useCallback<
      MediaDataRoomV3HandlersContextValue["deprovisionSeedAudience"]
    >(async () => {
      await session!.sendDataRoomStateActionRequest(dataRoomId, {
        deprovisionSeedAudiencePolicyIds: {},
        kind: "media",
      });
      updateAdvertiserDatasetHash(null);
    }, [dataRoomId, session, updateAdvertiserDatasetHash]);
    // Base audience handlers (ex. Publisher Audience)
    const provisionBaseAudience = useCallback<
      MediaDataRoomV3HandlersContextValue["provisionBaseAudience"]
    >(
      async ({
        dataLabId,
        usersDataset,
        segmentsDataset,
        demographicsDataset,
        embeddingsDataset,
      }) => {
        const baseAudiencePolicies = new Map<string, string>();
        // Provision matching dataset
        const matchingDataset = await client.getDataset(
          usersDataset.datasetHash
        );
        const matchingDatasetPolicyId = await createDatasetPolicy({
          dataRoomId,
          dataset: matchingDataset,
          session: session!,
          targetLeaf: "matching",
        });
        baseAudiencePolicies.set("matchingPolicyId", matchingDatasetPolicyId);
        // Provision segments dataset if present
        if (!isNil(segmentsDataset?.datasetHash)) {
          const dataset = await client.getDataset(segmentsDataset.datasetHash);
          const segmentsDatasetPolicyId = await createDatasetPolicy({
            dataRoomId,
            dataset,
            session: session!,
            targetLeaf: "segments",
          });
          baseAudiencePolicies.set("segmentsPolicyId", segmentsDatasetPolicyId);
        }
        // Provision demographics dataset if present
        if (!isNil(demographicsDataset?.datasetHash)) {
          const dataset = await client.getDataset(
            demographicsDataset.datasetHash
          );
          const demographicsDatasetPolicyId = await createDatasetPolicy({
            dataRoomId,
            dataset,
            session: session!,
            targetLeaf: "demographics",
          });
          baseAudiencePolicies.set(
            "demographicsPolicyId",
            demographicsDatasetPolicyId
          );
        }
        // Provision embeddings dataset if present
        if (!isNil(embeddingsDataset?.datasetHash)) {
          const dataset = await client.getDataset(
            embeddingsDataset.datasetHash
          );
          const embeddingsDatasetPolicyId = await createDatasetPolicy({
            dataRoomId,
            dataset,
            session: session!,
            targetLeaf: "embeddings",
          });
          baseAudiencePolicies.set(
            "embeddingsPolicyId",
            embeddingsDatasetPolicyId
          );
        }
        await session!.sendDataRoomStateActionRequest(dataRoomId, {
          kind: "media",
          provisionBaseAudiencePolicyIds: {
            ...Object.fromEntries(baseAudiencePolicies),
            matchingPolicyId: baseAudiencePolicies.get("matchingPolicyId")!,
          },
        });
        await apolloClient.mutate({
          mutation: ProvisionDataLabToMediaInsightsDcrDocument,
          variables: {
            input: {
              dataLabId,
              dataRoomId,
            },
          },
        });
        updatePublisherDatasetsHashes({
          demographicsDatasetHash: demographicsDataset?.datasetHash ?? null,
          embeddingsDatasetHash: embeddingsDataset?.datasetHash ?? null,
          matchingDatasetHash: usersDataset.datasetHash,
          segmentsDatasetHash: segmentsDataset?.datasetHash ?? null,
        });
      },
      [client, dataRoomId, session, updatePublisherDatasetsHashes, apolloClient]
    );
    const deprovisionBaseAudience = useCallback<
      MediaDataRoomV3HandlersContextValue["deprovisionBaseAudience"]
    >(async () => {
      await session!.sendDataRoomStateActionRequest(dataRoomId, {
        deprovisionBaseAudiencePolicyIds: {},
        kind: "media",
      });
      await apolloClient.mutate({
        mutation: DeprovisionDataLabFromMediaInsightsDcrDocument,
        variables: {
          dataRoomId,
        },
      });
      updatePublisherDatasetsHashes(null);
    }, [dataRoomId, session, updatePublisherDatasetsHashes, apolloClient]);
    // DataLab compatibility check
    const canProvisionDataLab = useCallback<
      MediaDataRoomV3HandlersContextValue["canProvisionDataLab"]
    >(
      async (dataLab: DataLab) => {
        const compatibilityErrors = await client.checkDataLabCompatibility(
          dataRoomId,
          [dataLab.id]
        );
        const compatibilityErrorsArray = compatibilityErrors.has(dataLab.id)
          ? (compatibilityErrors.get(dataLab.id) ?? [])
          : [];
        return {
          compatibilityErrors: compatibilityErrorsArray,
          isCompatible: compatibilityErrorsArray.length === 0,
        };
      },
      [client, dataRoomId]
    );
    const contextValue = useMemo<MediaDataRoomV3HandlersContextValue>(
      () => ({
        canProvisionDataLab,
        deprovisionBaseAudience,
        deprovisionSeedAudience,
        provisionBaseAudience,
        provisionSeedAudience,
        version: MediaDataRoomVersion.v3,
      }),
      [
        canProvisionDataLab,
        deprovisionBaseAudience,
        deprovisionSeedAudience,
        provisionBaseAudience,
        provisionSeedAudience,
      ]
    );
    return (
      <MediaDataRoomHandlersContext.Provider value={contextValue}>
        {children}
      </MediaDataRoomHandlersContext.Provider>
    );
  });
MediaDataRoomHandlersV3Wrapper.displayName = "MediaDataRoomHandlersV3Wrapper";

export default MediaDataRoomHandlersV3Wrapper;
